// backgroundClock.js — keeps time moving while the page is hidden.
//
// A hidden page (minimised, another tab in front, or — on Windows and
// macOS — a window fully covered by another) gets no requestAnimationFrame
// callbacks and has its timers clamped to once a second. A performer who
// puts Max or Reaper over the browser would lose the playhead, every
// anime-driven cue (trans, fade) and the OSC they stream. Dedicated
// workers are not throttled with the page, so while hidden a worker's
// 16 ms interval drives the loops that rAF would: anime's engine, the
// animation coordinator, the probe triggers, the clock's beats and the
// modulators (liveSignals.js, paramBinding.js), and in the Score the
// transport frame (RAF.js driveTransportWhileHidden). Visible again, rAF
// takes over and the worker goes quiet. Both apps start it: app.js and
// live.js.

const subs = new Set();
let worker = null;
let armed = false;

/** Called with performance.now() on every background tick while hidden. */
export function onBackgroundTick(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

export function isBackgroundClockActive() { return armed && document.hidden; }

function tickAll() {
  if (!document.hidden) return;              // rAF is back — stay out of its way
  const now = performance.now();
  for (const fn of subs) {
    try { fn(now); } catch (err) { console.warn("[bgClock] tick failed:", err); }
  }
}

function startWorker() {
  if (worker) return;
  try {
    const src = `let id = null;
      onmessage = (e) => {
        if (e.data === "start" && !id) id = setInterval(() => postMessage(0), 16);
        if (e.data === "stop" && id) { clearInterval(id); id = null; }
      };`;
    worker = new Worker(URL.createObjectURL(new Blob([src], { type: "application/javascript" })));
    worker.onmessage = tickAll;
    worker.postMessage("start");
    armed = true;
    console.log("[bgClock] page hidden — worker clock driving the loops");
  } catch (err) {
    console.warn("[bgClock] no Worker available; hidden page will throttle:", err);
  }
}

function stopWorker() {
  if (!worker) return;
  worker.postMessage("stop");
  worker.terminate();
  worker = null;
  armed = false;
  console.log("[bgClock] page visible — rAF resumes");
}

// anime's engine is a rAF loop that stops dead when hidden; ticking it
// here keeps trans(), fade() and the other tweens on time.
function driveAnime() {
  try { window.anime?.engine?.update(); } catch (err) { console.warn("[bgClock] anime tick:", err); }
}

export function initBackgroundClock() {
  // anime pauses its engine the moment the page hides; we keep it
  // running instead (its rAF stalls, our ticks stand in for it)
  if (window.anime) window.anime.engine.pauseOnDocumentHidden = false;
  subs.add(driveAnime);
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) startWorker();
    else stopWorker();
  });
  if (document.hidden) startWorker();
  window.oscillaBackgroundClock = { onBackgroundTick, isActive: isBackgroundClockActive };
  // modules loaded before it (liveSignals, paramBinding, probeTriggers) subscribe now
  window.dispatchEvent(new Event("oscilla:background-clock-ready"));
}
